import React, { ChangeEvent, Component, ReactNode } from 'react';
import Icon from '../../../icons/Icon';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { RootState } from '../../../redux/store';
import { ListState } from '../../../redux/reducers/ListReducer';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { changeList, changeListData, changeListIsUpdate, clearList } from '../../../redux/actions/ListActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { CommonConnectionModel } from '../../../domain/connection/ConnectionDomains';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import RoleHelper from '../../../RoleHelper';
import FormicaInput from '../../custom/FormicaInput';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import FormicaLabel from '../../custom/FormicaLabel';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';

class ListQueryCreatorComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private connectionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  public render(): ReactNode {
    const {
      list: { list },
    } = this.props;

    return (
      <div className={'flex w-full h-full lg:flex-row flex-col overflow-y-scroll pb-20 lg:pb-0'}>
        <div className={'flex w-full lg:w-1/2 h-full items-center justify-center lg:py-0'}>
          <section className={'flex flex-col h-full items-center gap-y-2 justify-center col-span-1 lg:pr-10 lg:pl-5 lg:border-r lg:border-f-black-5'}>
            <Icon icon={'info'} className={'text-primary'} size={40} />
            <h5 className={'text-primary font-bold text-xl'}>{LanguageUtil.get({ id: 'pages.list.information', defaultMessage: 'Information' })}</h5>
            <p className={'text-center text-primary lg:w-1/2 font-semibold'}>
              {LanguageUtil.get({
                id: 'pages.list.thisFlowIsDeployed',
                defaultMessage: 'This flow is deployed. If you undeploy this flow you can rollback from flow history page.',
              })}
            </p>
            <form onSubmit={(e) => e.preventDefault()} className={'flex flex-col gap-y-5 w-full'}>
              <section>
                <FormicaLabel label={LanguageUtil.get({ id: 'common.label.connection', defaultMessage: 'Connection' })} />
                <FormicaSearchSelectBox
                  value={list.connectionName}
                  rounded
                  data-testid={`${ComponentName.LIST}_connection_select_container`}
                  options={this.connectionOptions()}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.connection', defaultMessage: 'Connection' })}
                  disabled={!RoleHelper.canEdit('QueryList')}
                  onChange={this.onChangeConnection}
                />
              </section>
              <section>
                <FormicaLabel label={LanguageUtil.get({ id: 'common.label.cron', defaultMessage: 'Cron' })} />
                <FormicaInput
                  value={list.cronExpression}
                  onChange={this.onChangeCron}
                  name={'cron'}
                  type={'text'}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.cron', defaultMessage: 'Cron' })}
                  disabled={!RoleHelper.canEdit('QueryList')}
                  className={''}
                  data-testid={`${ComponentName.LIST}_cron_input`}
                />
              </section>
            </form>
          </section>
        </div>
        <div className={'flex flex-col w-full lg:w-1/2 lg:pt-10 h-full items-start lg:gap-y-5 justify-center'}>
          <div className={'flex flex-col items-center justify-end w-full h-full'}>
            <Icon icon={'question'} className={'text-primary'} size={40} />
            <h5 className={'text-primary font-bold text-xl text-bold'}>{LanguageUtil.get({ id: 'pages.list.query', defaultMessage: 'Query' })}</h5>
            <p className={'text-center text-primary lg:w-1/2 font-semibold'}>
              {LanguageUtil.get({
                id: 'pages.list.loremFlowIsDeployed',
                defaultMessage: 'Lorem flow is deployed. If you undeploy this flow you can rollback from flow history',
              })}
            </p>
          </div>
          <div className={'flex flex-col items-center justify-center  w-full h-full'}>
            <form onSubmit={(e) => e.preventDefault()} className={'flex h-auto items-start justify-start flex-col gap-y-1 w-full lg:pl-10 lg:pr-5'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'pages.list.query', defaultMessage: 'Query' })} />
              <CodeEditor
                value={list.query}
                onChange={this.onChangeQuery}
                language='sql'
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.pleaseEnterQuery', defaultMessage: 'Please enter Query' })}
                padding={15}
                style={{
                  background: 'rgb(252 252 252 / var(--tw-bg-opacity))',
                  border: '1px solid rgb(224 224 224)',
                  borderRadius: '0.25rem',
                }}
                data-testid={`${ComponentName.LIST}_query_editor`}
                disabled={!RoleHelper.canEdit('QueryList')}
                className={'flex h-full lg:min-h-[240px] w-full'}
              />
            </form>
          </div>
        </div>
      </div>
    );
  }

  public componentDidMount() {
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = async () => {
    const connectionQuery = query.QueryBuilder.select().from(CollectionNames.CONNECTIONS).build();
    this.connectionSubscription = subscribe.Formica.subscribe(CollectionNames.CONNECTIONS, connectionQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.connectionSubscription]);
  };

  private stopTracker = async () => {
    await this.connectionSubscription?.stop();
    this.computation?.stop();
  };

  private tracker = async () => {
    if (this.connectionSubscription?.ready()) {
      const connectionQuery = query.QueryBuilder.select().from(CollectionNames.CONNECTIONS).build();
      const connections: any = await this.repository.findMany(connectionQuery);
      this.setState({ connections });
    }
  };

  private onChangeCron = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    list.cronExpression = e.target.value;
    changeList(list);
  };

  private onChangeConnection = (e: FormicaSelectBoxItem) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    list.connectionName = e.value;
    changeList(list);
  };

  private onChangeQuery = (e: any) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    list.query = e.target.value;
    changeList(list);
  };

  private connectionOptions = (): FormicaSelectBoxItem[] => {
    const { connections } = this.state;
    return connections.map((connection: CommonConnectionModel) => {
      return {
        label: connection.name,
        value: connection.name,
        icon: connection.databaseType,
      };
    });
  };
}

interface Props extends ReduxProps {}

class State {
  connections: CommonConnectionModel[] = [];
}

const mapStateToProps = (state: RootState) => ({
  list: state.list as ListState,
});
const mapDispatchToProps = { enterComponent, leaveComponent, changeList, changeListIsUpdate, changeListData, clearList };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ListQueryCreatorComponent);
